import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { debounceTime, Subscription } from 'rxjs';
import { generateUUID } from 'src/app/core/generators/generateUUID';
import { PatioService } from 'src/app/services/PatioService/patio.service';
import { CargaService } from 'src/app/services/CargaService/carga.service';
import { ITabelaCarreta } from 'src/app/pages/servicos/listagem-servicos/listagem-servicos-model';
import { CarretaService } from 'src/app/services/CarretaService/carreta.service';
import { AuthService } from 'src/app/services/AuthService/auth.service';
import { SwalAlertService } from 'src/app/services/SwalAlertService/swalAlert.service';
import Swal from 'sweetalert2';
import { PatioFormModalComponent } from '../patio-form-modal/patio-form-modal.component';
import { IGetCarreta } from 'src/app/services/CarretaService/carreta.model';

interface IOptions {
  name: string;
  value: string | number;
}
export type PatioItem = {
  TX_TIPO_SRVC: string;
  TX_CRGA: string;
  PK_PTIO: number;
  QT_CAPA: number;
  QT_AGND: number;
  NU_TRNO: number;
  FK_CRGA: number;
  NM_DTNR: string;
};

@Component({
  selector: 'app-listagem-parametrizacao',
  templateUrl: './listagem-parametrizacao.component.html',
  styleUrls: ['./listagem-parametrizacao.component.scss']
})
export class ListagemParametrizacaoComponent implements OnInit {
  guid: string = generateUUID();
  /** Serviço "Pátio" em T_SRVC. */
  private readonly servicoPatioId = 128;
  cargaOptions = [{ name: '', value: '' }];
  patioService = inject(PatioService);
  dialog = inject(MatDialog);
  cargaService = inject(CargaService);
  private sub$ = new Subscription();
  items: PatioItem[] = [];
  isLoading: boolean = false;
  form = this._inicializarFormulario();
  numeroPagina: number = 0;
  tableData: ITabelaCarreta[] | Array<any> = [];
  maxPages: number = 1;
  expandirCardsMobile: boolean = true;

  id: number = Number(localStorage.getItem('id'));

  tipoCarretaOptions: IOptions[] = [
    {
      name: '',
      value: ''
    }
  ];

  editForm = this.fb.group({
    PK_PTIO: [0, { nonNullable: true, validators: [Validators.required] }],
    NU_TRNO: [0, { nonNullable: true, validators: [Validators.required] }],
    QT_CAPA: [
      0,
      {
        nonNullable: true,
        validators: [Validators.required, Validators.min(0)]
      }
    ],
    QT_AGND: [
      0,
      {
        nonNullable: true,
        validators: [Validators.required, Validators.min(0)]
      }
    ],
    FK_CRGA: [null as number | null]
  });

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private carretaService: CarretaService,
    private authService: AuthService,
    private swalAlert: SwalAlertService
  ) {}

  private _inicializarFormulario() {
    const form = this.fb.group({
      TX_PLCA_CRRT: this.fb.control('', {
        nonNullable: true,
        validators: [Validators.required]
      })
    });
    return form;
  }
  onMenu(item: PatioItem): void {
    Swal.fire({
      title: 'Ações',
      showCancelButton: true,
      cancelButtonText: 'Fechar',
      confirmButtonText: 'Editar',
      confirmButtonColor: '--primary-color'
    }).then(result => {
      if (result.isConfirmed) {
        this.openEditModal(item.PK_PTIO);
      }
    });
  }
  editarItem(item: any): void {
    this.openEditModal(item.PK_PTIO);
  }

  excluirItem(item: PatioItem): void {
    Swal.fire({
      title: 'Confirmar exclusão',
      text: 'Deseja realmente excluir este pátio?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Excluir',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33'
    }).then(result => {
      if (result.isConfirmed) {
        this.sub$.add(
          this.patioService.DeletePatio(item.PK_PTIO).subscribe({
            next: () => {
              this.swalAlert.showAlert(
                'Sucesso!',
                'Pátio excluído com sucesso.',
                'success',
                true,
                false,
                { confirmButtonColor: 'green', confirmButtonText: 'Ok' },
                () => this.getPatios(1)
              );
            },
            error: error => {
              Swal.fire({
                icon: 'error',
                title: 'Erro ao excluir',
                text: error.error?.message || 'Não foi possível excluir o pátio.'
              });
            }
          })
        );
      }
    });
  }
  openEditModal(id: number): void {
    const current = this.items.find(x => x.PK_PTIO === id);

    if (!current) {
      Swal.fire({ icon: 'warning', title: 'Item não encontrado' });
      return;
    }

    const ref = this.dialog.open(PatioFormModalComponent, {
      width: '520px',
      disableClose: true,
      data: { mode: 'edit', item: current }
    });

    this.sub$.add(
      ref.afterClosed().subscribe(result => {
        if (!result) return;
        this.handleModalResult(result);
      })
    );
  }
  private handleModalResult(result: {
    mode: 'create' | 'edit';
    value: any;
  }): void {
    if (!result) return;

    const v = result.value;

    if (result.mode === 'create') {
      const fkDtnr = Number(v.FK_DTNR);
      const fkCrga = Number(v.FK_CRGA);
      if (fkCrga <= 0) {
        Swal.fire({
          icon: 'warning',
          title: 'Carga obrigatória',
          text: 'Selecione uma carga antes de salvar o pátio.'
        });
        return;
      }

      this.guid = generateUUID();
      const payload = {
        QT_CAPA: Number(v.QT_CAPA) || 0,
        QT_AGND: Number(v.QT_AGND) || 0,
        NU_TRNO: Number(v.NU_TRNO) || 0,
        FK_SRVC: this.servicoPatioId,
        FK_CRGA: fkCrga,
        FK_DTNR: fkDtnr > 0 ? fkDtnr : null
      };

      this.sub$.add(
        this.patioService.CreatePatio(payload, this.guid).subscribe({
          next: () => {
            this.swalAlert.showAlert(
              'Sucesso!',
              'Cadastro realizado com sucesso.',
              'success',
              true,
              false,
              { confirmButtonColor: 'green', confirmButtonText: 'Ok' },
              () => this.getPatios(1)
            );
          },
          error: error => {
            console.error('Erro ao cadastrar:', error);
            Swal.fire({
              icon: 'error',
              title: 'Erro ao cadastrar',
              text:
                error.error?.Error ||
                error.error?.Message ||
                error.error?.message ||
                error.message ||
                'Verifique os dados e tente novamente'
            });
          }
        })
      );

      return;
    }

    const pk = v.PK_PTIO;

    const body = {
      QT_CAPA: Number(v.QT_CAPA) || 0,
      QT_AGND: Number(v.QT_AGND) || 0,
      NU_TRNO: Number(v.NU_TRNO) || 0,
      FK_CRGA: Number(v.FK_CRGA) || 0,
      FK_DTNR: Number(v.FK_DTNR) || 0
    };

    this.sub$.add(
      this.patioService.EditPatio(pk, body).subscribe({
        next: response => {
          this.swalAlert.showAlert(
            'Sucesso!',
            'Edição realizada com sucesso.',
            'success',
            true,
            false,
            { confirmButtonColor: 'green', confirmButtonText: 'Ok' },
            () => this.getPatios(1)
          );
        },
        error: error => {
          console.error('Erro ao editar:', error);
          Swal.fire({
            icon: 'error',
            title: 'Erro ao salvar',
            text:
              error.error?.Error ||
              error.error?.Message ||
              error.error?.message ||
              error.message ||
              'Verifique os dados e tente novamente'
          });
        }
      })
    );
  }
  ngOnInit() {
    this.getCarreta();
    this.getPatios(1);
    this.getCarga();
  }

  ngAfterViewInit(): void {
    this.form.valueChanges.pipe(debounceTime(300)).subscribe(() => {
      this.getCarreta();
    });
  }

  onModalCadastro(): void {
    const ref = this.dialog.open(PatioFormModalComponent, {
      width: '520px',
      disableClose: true,
      data: {
        mode: 'create',
        item: {
          NU_TRNO: 0,
          QT_CAPA: 0,
          QT_AGND: 0,
          FK_CRGA: 0
        }
      }
    });

    this.sub$.add(
      ref.afterClosed().subscribe(result => {
        if (!result) return;
        this.handleModalResult(result);
      })
    );
  }

  mudarPagina(pagina: number) {
    this.numeroPagina = pagina;
    this.getCarreta(pagina + 1);
  }

  formatarCampos(campo: string | number | Date): string {
    return campo === null || campo === '' ? 'Não Informado' : String(campo);
  }

  getCarreta(pageNumber?: number) {
    this.isLoading = true;
    const TX_PLCA_CRRT = String(this.form.controls.TX_PLCA_CRRT.value);
    this.authService.dadoLogin$.subscribe(({ idEmpresa }) => {
      this.carretaService
        .GetCarreta({
          placa: TX_PLCA_CRRT,
          tipoCarreta: 0,
          idEmpresa,
          pageNumber,
          pageSize: 20,
          status: 0
        })
        .subscribe({
          next: (response: IGetCarreta) => {
            this.isLoading = false;
            this.tableData = response.ITEMS.map(item => {
              return {
                codigo: item.PK_CRRT ?? 0,
                placaCarreta: String(item.TX_PLCA_CRRT),
                tipoCarreta: item.NM_TIPO_CRRT,
                empresa: item.FK_EPSA,
                ativo: item.NU_STTS_CRRT == 1 ? false : true,
                situacao: item.NU_STTS_CRRT,
                externo: 0
              };
            });
            this.maxPages = response.TOTAL_PAGES;
          },
          error: (_error: any) => {
            this.isLoading = false;
          }
        });
    });
  }

  permitirEditar(item: any): boolean {
    const status: boolean = item.situacao == 1 ? true : false;
    return status;
  }

  idEmpresa = '1';

  getPatios(page: number): void {
    this.isLoading = true;

    this.sub$.add(
      this.patioService.GetPatios(this.idEmpresa, page, 1).subscribe({
        next: (res: PatioItem[]) => {
          this.items = (res ?? [])
            .slice()
            .sort((a, b) =>
              (a.NM_DTNR || '').localeCompare(b.NM_DTNR || '', 'pt-BR', {
                sensitivity: 'base'
              })
            );
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        }
      })
    );
  }

  getCarga() {
    this.isLoading = true;

    this.cargaService.GetCarga().subscribe({
      next: res => {
        this.cargaOptions = res.map((t: any) => ({
          name: t.TX_CRGA,
          value: t.PK_CRGA
        }));
      }
    });
  }
}

